import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import FadeSlide from "./transitions/FadeSlide";
import GoldWipe from "./transitions/GoldWipe";
import ParticleDissolve from "./transitions/ParticleDissolve";
import "../../styles/PageTransitions.css";

// Route-based transition mapping
const getTransitionType = (fromPath, toPath) => {
  // Premium routes get GoldWipe transition
  const premiumRoutes = ["/checkout", "/wallet", "/profile-complete"];

  // Special routes get ParticleDissolve
  const specialRoutes = ["/drops", "/vip", "/exclusive"];

  // Check if navigating to a premium route
  if (premiumRoutes.some(route => toPath.startsWith(route))) {
    return "goldWipe";
  }

  // Check if navigating to a special route
  if (specialRoutes.some(route => toPath.startsWith(route))) {
    return "particleDissolve";
  }

  // Product detail from shop gets smooth fade
  if (fromPath.startsWith("/shop") && toPath.startsWith("/product/")) {
    return "fadeSlide";
  }

  // Default transition
  return "fadeSlide";
};

const PageTransition = ({ children }) => {
  const location = useLocation();
  const [prevPath, setPrevPath] = useState(location.pathname);
  const [transitionType, setTransitionType] = useState("fadeSlide");
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Check for manual transition hint (from usePageTransition hook)
    const transitionHint = location.state?._transitionHint ||
                          sessionStorage.getItem('lavvissa_transition_hint');

    // Determine transition type based on hint or route change
    const newTransitionType = transitionHint ||
                             getTransitionType(prevPath, location.pathname);

    setTransitionType(newTransitionType);

    // Preload strategy: mark as ready after a brief moment
    // This prevents content flash during transition
    setIsReady(false);
    const readyTimer = setTimeout(() => {
      setIsReady(true);
    }, 50);

    return () => clearTimeout(readyTimer);
  }, [location.pathname, location.state, prevPath]);

  useEffect(() => {
    // Update previous path after transition
    const pathTimer = setTimeout(() => {
      setPrevPath(location.pathname);
    }, 100);

    return () => clearTimeout(pathTimer);
  }, [location.pathname]);

  // Scroll to top on route change (smooth scroll for luxury feel)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Select the appropriate transition component
  const TransitionComponent = {
    fadeSlide: FadeSlide,
    goldWipe: GoldWipe,
    particleDissolve: ParticleDissolve,
  }[transitionType];

  return (
    <AnimatePresence mode="wait" initial={false}>
      <TransitionComponent key={location.pathname} isReady={isReady}>
        {children}
      </TransitionComponent>
    </AnimatePresence>
  );
};

export default PageTransition;
